/**
 * Phase 2 (Feb 2026) — Home screen.
 *
 * The new default landing surface for /app. Central hub:
 *   1. Profi-Chat as the main entry point (not a route list).
 *   2. Traffic-light tiles for running campaigns (§7.3 concept).
 *   3. "Was VIONA für dich erledigt hat" chronicle.
 *   4. Open decisions (approvals) inline.
 *   5. Three control-actions (cancel run / pause autopilot / stop delivery).
 *
 * Mobile-first (works from 375px). The Profi-Chat lives full-width at
 * the top of the mobile stack.
 */
import ProfiChat from "../../components/app/ProfiChat";
import CampaignTrafficLightTiles from "../../components/app/CampaignTrafficLightTiles";
import VionaChronicle from "../../components/app/VionaChronicle";
import OpenDecisions from "../../components/app/OpenDecisions";
import ControlActions from "../../components/app/ControlActions";
import OnboardingBanner from "../../components/app/OnboardingBanner";
import AggregationConsumedBadge from "../../components/app/AggregationConsumedBadge";
import { auth } from "../../lib/api";
import { Navigate } from "react-router-dom";

export default function Home() {
  const user = auth.getUser() || {};
  // Session 7 Phase 1: a platform_owner reaching /app/home directly
  // (bookmark, deep-link) must be bounced to the operator console
  // BEFORE any tenant-scoped queries fire — otherwise the AppLayout
  // triggers ``campaigns.read`` and the user sees a permission toast
  // that is a UX defect, not a real access problem.
  if (user.role === "platform_owner" || user.role === "operator") {
    return <Navigate to="/operator/dashboard" replace />;
  }
  const firstName = (user.name || user.email || "").split(/[.\s@]/)[0] || "";
  return (
    <div data-testid="home-page" className="space-y-6 md:space-y-8">
      <OnboardingBanner />

      <header data-testid="home-header" className="space-y-1">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
          {firstName ? `Hallo ${firstName}.` : "Willkommen."}
        </h1>
        <p className="text-sm text-muted-foreground">
          Sag VIONA, was du erreichen willst. Alles Weitere passiert
          transparent — mit deiner Freigabe, wo es zählt.
        </p>
      </header>

      {/* Profi-Chat lives at the top — it IS the primary entry point. */}
      <ProfiChat variant="hero" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CampaignTrafficLightTiles />
        <OpenDecisions />
      </div>

      <ControlActions />

      <div
        data-testid="home-budget-tile"
        className="rounded-md border bg-card px-4 py-3"
      >
        <div className="text-xs font-medium mb-1 text-muted-foreground">
          Heutiger Erhöhungs-Rahmen
        </div>
        <AggregationConsumedBadge variant="block" />
      </div>

      <VionaChronicle />
    </div>
  );
}
